import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  CircularProgress,
  FormControlLabel,
  Switch,
  Checkbox,
  Card,
  CardContent,
  CardActions,
  Chip,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import * as mappingApi from '../../services/mappingApi';
import type { ProjectRecipeSummary } from '../../services/types';

interface RecipePickerDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (recipe: ProjectRecipeSummary, setAsDefault: boolean) => void;
  /** Current project ID — excluded from the listing */
  currentProjectId: string;
  /** Current project's target_schema_id — used for filtering */
  currentTargetSchemaId?: string | null;
}

export default function RecipePickerDialog({
  open,
  onClose,
  onSelect,
  currentProjectId,
  currentTargetSchemaId,
}: RecipePickerDialogProps) {
  const [recipes, setRecipes] = useState<ProjectRecipeSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterSameSchema, setFilterSameSchema] = useState(true);
  const [setAsDefault, setSetAsDefault] = useState(false);

  const fetchRecipes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await mappingApi.listApprovedRecipes({
        excludeProjectId: currentProjectId,
        targetSchemaId: filterSameSchema && currentTargetSchemaId ? currentTargetSchemaId : undefined,
        limit: 20,
      });
      setRecipes(result);
    } catch (e: any) {
      setError(e.message || 'Failed to load recipes');
    } finally {
      setLoading(false);
    }
  }, [currentProjectId, currentTargetSchemaId, filterSameSchema]);

  useEffect(() => {
    if (open) {
      fetchRecipes();
    }
  }, [open, fetchRecipes]);

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  const hasSchemaMismatch = (recipe: ProjectRecipeSummary) => {
    return (
      currentTargetSchemaId &&
      recipe.target_schema_id &&
      recipe.target_schema_id !== currentTargetSchemaId
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SearchIcon color="primary" />
        Bootstrap from Another Project
      </DialogTitle>

      <DialogContent dividers>
        {/* Filter toggle */}
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <FormControlLabel
            control={
              <Switch
                checked={filterSameSchema}
                onChange={(_, checked) => setFilterSameSchema(checked)}
                size="small"
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                Same target schema only
              </Typography>
            }
          />
          <Typography variant="caption" color="text.secondary">
            {recipes.length} recipe{recipes.length !== 1 ? 's' : ''} found
          </Typography>
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && recipes.length === 0 && (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No approved recipes found in other projects.
            </Typography>
            {filterSameSchema && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Try disabling the "Same target schema only" filter.
              </Typography>
            )}
          </Box>
        )}

        {!loading &&
          recipes.map((recipe) => (
            <Card
              key={recipe.recipe_id}
              variant="outlined"
              sx={{
                mb: 1.5,
                borderColor: hasSchemaMismatch(recipe) ? 'warning.light' : 'divider',
                '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                transition: 'border-color 0.2s, background-color 0.2s',
              }}
            >
              <CardContent sx={{ pb: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {recipe.project_name || 'Unknown Project'}
                  </Typography>
                  {hasSchemaMismatch(recipe) && (
                    <Chip
                      icon={<WarningAmberIcon />}
                      label="Different target schema"
                      size="small"
                      color="warning"
                      variant="outlined"
                      sx={{ fontSize: 11 }}
                    />
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Typography variant="body2" color="text.secondary">
                    {recipe.mapping_count ?? 0} mappings
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Approved {formatDate(recipe.approved_at)}
                  </Typography>
                  {recipe.ai_confidence != null && (
                    <Typography variant="body2" color="text.secondary">
                      {Math.round(recipe.ai_confidence * 100)}% confidence
                    </Typography>
                  )}
                  {recipe.generation_mode && (
                    <Chip
                      label={recipe.generation_mode}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: 11 }}
                    />
                  )}
                </Box>
              </CardContent>
              <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => onSelect(recipe, setAsDefault)}
                >
                  Select
                </Button>
              </CardActions>
            </Card>
          ))}

        {/* Set as default baseline checkbox */}
        {recipes.length > 0 && (
          <Box sx={{ mt: 2, borderTop: 1, borderColor: 'divider', pt: 1.5 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={setAsDefault}
                  onChange={(_, checked) => setSetAsDefault(checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="body2" color="text.secondary">
                  Set as default baseline for this project
                </Typography>
              }
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}
